import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, FileText, UploadCloud } from "lucide-react";
import { Seo } from "@/components/Seo";
import { trackButton, trackLink, trackPageEvent } from "@/lib/analytics";
import "./TryUpload.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Feature flag (customer-approved default: OFF). Flip to true to require the
// "no real patient information" confirmation before processing.
const REQUIRE_CONFIRMATION = false;

const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".tif", ".tiff"];
const SAMPLE_URL = "/samples/sample-cms-1500.pdf";
const REDIRECT_FALLBACK = "/demo/hcfa-cms-1500";
const TIMEOUT_MS = 90000;

const ERRORS = {
  type: "This file type is not supported. Use a PDF, JPG, PNG, or TIFF.",
  size: "This file is over 20 MB. Upload a smaller scan.",
  server: "We could not process this claim. Try again, or use a sample claim.",
};

const STEPS = [
  "Reading the form",
  "Checking NPI, ICD-10, and CPT",
  "Building the 837P file",
  "Opening your demo",
];

const TryUpload = () => {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | processing | ready
  const [error, setError] = useState(null); // type | size | server
  const [step, setStep] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const inputRef = useRef(null);
  const pollTimer = useRef(null);
  const cancelled = useRef(false);

  useEffect(() => {
    trackPageEvent("upload_page_view");
    return () => {
      cancelled.current = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const validateFile = (f) => {
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext) && !["application/pdf", "image/jpeg", "image/png", "image/tiff"].includes(f.type)) {
      return "type";
    }
    if (f.size > MAX_BYTES) return "size";
    return null;
  };

  const onFileChosen = (f, isSample = false) => {
    if (!f) return;
    const err = validateFile(f);
    setError(err);
    setFile(err ? null : f);
    if (!err) trackButton(isSample ? "sample_claim_selected" : "claim_file_selected");
    else trackPageEvent("process_failed", { reason: err });
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const fail = (reason) => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setPhase("idle");
    setError("server");
    trackPageEvent("process_failed", { reason });
  };

  const processFile = async (f) => {
    if (!f || phase === "processing") return;
    cancelled.current = false;
    setPhase("processing");
    setStep(0);
    setError(null);
    trackButton("process_clicked");

    let jobId = null;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API}/demo/upload`, { method: "POST", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        fail(j.detail === "unsupported_type" ? "type" : j.detail === "too_large" ? "size" : "server_error");
        return;
      }
      jobId = (await res.json()).job_id;
    } catch {
      fail("server_error");
      return;
    }

    const started = Date.now();
    const poll = async () => {
      if (cancelled.current) return;
      try {
        const r = await fetch(`${API}/demo/status/${jobId}`);
        const j = await r.json();
        if (j.status === "succeeded") {
          setStep(3);
          trackPageEvent("process_succeeded");
          trackPageEvent("demo_redirected");
          setPhase("ready"); // visible only if navigation is blocked
          window.location.assign(j.redirect || REDIRECT_FALLBACK);
          return;
        }
        if (j.status === "failed") {
          fail("server_error");
          return;
        }
        if (typeof j.step === "number") setStep(Math.min(j.step, 3));
      } catch {
        // transient poll errors are tolerated until the timeout
      }
      if (Date.now() - started > TIMEOUT_MS) {
        fail("timeout");
        return;
      }
      pollTimer.current = setTimeout(poll, 1200);
    };
    pollTimer.current = setTimeout(poll, 1200);
  };

  const useSample = async () => {
    if (phase === "processing") return;
    try {
      const res = await fetch(SAMPLE_URL);
      const blob = await res.blob();
      const f = new File([blob], "sample-cms-1500.pdf", { type: "application/pdf" });
      onFileChosen(f, true);
      processFile(f);
    } catch {
      setError("server");
    }
  };

  const canSubmit = !!file && (!REQUIRE_CONFIRMATION || confirmed) && phase !== "processing";

  return (
    <div className="try-page">
      <Seo
        title="Try Hewto.ai on your own claim"
        description="Upload a claim and see it as 837P. Hewto.ai reads your CMS-1500, checks every field, and opens the results in a live demo."
        noindex
      />
      <div className="try-bg-grid" aria-hidden="true" />
      <div className="try-bg-arcs" aria-hidden="true" />

      <header className="try-topbar">
        <Link to="/" className="try-logo" data-testid="try-logo-link" aria-label="Hewto.ai home">
          <img className="try-logo-dark" src="/logo.png" alt="Hewto.ai" />
          <img className="try-logo-white" src="/logo-white.png" alt="" aria-hidden="true" />
        </Link>
        <Link
          to="/contact-sales"
          className="try-sales-link"
          data-testid="try-talk-to-sales"
          onClick={() => trackLink("talk_to_sales_clicked")}
        >
          Talk to sales
        </Link>
      </header>

      <main className="try-main">
        <div className="try-left">
          <h1 className="try-h1">Upload a claim. See it as 837P.</h1>
          <p className="try-lead">
            <strong>Experience this with your own claims.</strong>{" "}
            <span>
              Hewto.ai reads your CMS-1500, checks every field, and opens the results in a live
              demo. No login or sign-up required.
            </span>
          </p>

          <h2 className="try-label">How it works</h2>
          <ol className="try-steps">
            {[
              ["Upload a claim", "A scan, fax, photo, or PDF of a CMS-1500."],
              ["We read and validate it", "Every box is extracted, checked, and scored."],
              ["Review it in your demo", "You land directly on your processed claim."],
            ].map(([t, d], i) => (
              <li key={t}>
                <span className="try-stepnum" aria-hidden="true"><span>{i + 1}</span></span>
                <span>
                  <strong>{t}</strong>
                  <span>{d}</span>
                </span>
              </li>
            ))}
          </ol>

          <h2 className="try-label">What you will see</h2>
          <ul className="try-checks">
            {[
              "Structured fields with per-field confidence",
              "NPI, ICD-10, and CPT validation results",
              "837P-ready output for your clearinghouse",
            ].map((t) => (
              <li key={t}>
                <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
                {t}
              </li>
            ))}
          </ul>

          <h2 className="try-label">Forms we read</h2>
          <div className="try-pills">
            <span className="try-pill">CMS-1500</span>
            <span className="try-pill">UB-04</span>
            <span className="try-pill">ADA J430D</span>
          </div>
        </div>

        <div className="try-right">
          <div className="try-card" data-testid="try-upload-card">
            <h2 className="try-card-title">Try it on your own claim</h2>
            <p className="try-card-sub">No login or sign-up required.</p>

            {phase === "processing" || phase === "ready" ? (
              <div data-testid="try-processing">
                <div className="try-proc-file">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                  <span>{file?.name}</span>
                </div>
                <ul className="try-proc-steps" aria-live="polite" data-testid="try-steps">
                  {STEPS.map((s, i) => {
                    const state = phase === "ready" ? "done" : i < step ? "done" : i === step ? "active" : "todo";
                    return (
                      <li key={s} className={state} data-testid={`try-step-${i}`}>
                        <span className="try-stepicon" aria-hidden="true">
                          {state === "done" ? (
                            <Check className="done-check h-4 w-4" strokeWidth={3} />
                          ) : state === "active" ? (
                            <span className="try-spin" />
                          ) : (
                            <span className="todo-dot" />
                          )}
                        </span>
                        {s}
                      </li>
                    );
                  })}
                </ul>
                {phase === "ready" && (
                  <div data-testid="try-ready">
                    <p className="try-ready">Your results are ready.</p>
                    <div className="try-ready-actions">
                      <Link to={REDIRECT_FALLBACK} className="try-quiet-link" data-testid="try-ready-link">
                        Open your demo
                      </Link>
                      <button
                        type="button"
                        className="try-text-btn"
                        data-testid="try-process-another"
                        onClick={() => {
                          setPhase("idle");
                          removeFile();
                        }}
                      >
                        Process another claim
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="try-upload-form">
                <div
                  className={`try-drop${dragOver ? " over" : ""}`}
                  data-testid="try-dropzone"
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    onFileChosen(e.dataTransfer.files?.[0]);
                  }}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    className="try-file-input"
                    accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
                    aria-label="Choose a claim file"
                    data-testid="try-file-input"
                    onChange={(e) => onFileChosen(e.target.files?.[0])}
                  />
                  <UploadCloud className="try-drop-icon h-8 w-8" aria-hidden="true" />
                  <p className="try-drop-text">Drag a claim here or choose a file</p>
                  <p className="try-drop-hint">PDF, JPG, PNG, or TIFF, up to 20 MB</p>
                </div>

                {file && (
                  <div className="try-filerow" data-testid="try-filerow">
                    <span className="try-filename">
                      <FileText className="h-4 w-4" aria-hidden="true" />
                      {file.name}
                    </span>
                    <button type="button" className="try-remove" data-testid="try-remove-file" onClick={removeFile}>
                      Remove
                    </button>
                  </div>
                )}

                {REQUIRE_CONFIRMATION && (
                  <label className="try-confirm" data-testid="try-confirm">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => {
                        setConfirmed(e.target.checked);
                        if (e.target.checked) trackButton("hipaa_notice_checkbox_checked");
                      }}
                    />
                    I confirm this claim contains no real patient information
                  </label>
                )}

                {error && (
                  <p className="try-error" role="alert" data-testid="try-error">
                    {ERRORS[error]}
                  </p>
                )}

                <button
                  type="button"
                  className="try-submit"
                  data-testid="try-process-btn"
                  disabled={!canSubmit}
                  onClick={() => processFile(file)}
                >
                  Process my claim
                </button>

                <div className="try-sample-row">
                  <span>No claim handy?</span>
                  <button type="button" className="try-text-btn" data-testid="try-sample-btn" onClick={useSample}>
                    Use a sample claim
                  </button>
                </div>
              </div>
            )}

            <p className="try-notice" data-testid="try-hipaa-notice">
              <strong>This demo is not HIPAA compliant.</strong>{" "}
              <span>Upload only sample or de-identified claims. Do not upload real patient information.</span>
            </p>

            <hr className="try-divider" />
            <p className="try-quiet">
              Prefer a guided walkthrough?{" "}
              <Link
                to="/contact-sales"
                className="try-quiet-link"
                data-testid="try-book-demo"
                onClick={() => trackLink("book_demo_clicked")}
              >
                Book a demo
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TryUpload;
